<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte'
  import { X } from 'lucide-svelte'
  
  export let isOpen = false
  export let fileName = ''
  export let content = ''
  
  const dispatch = createEventDispatcher()
  
  // Export settings
  let includeTitle = false
  let pageSize = 'Letter'
  let landscape = false
  let margin = 'Default'
  let downscaleValue = 100
  let showTooltip = false
  let tooltipPosition = { x: 0, y: 0 }
  let tooltipValue = 100
  let isHoveringThumb = false
  
  const pageSizeOptions = [
    'A3',
    'A4',
    'A5',
    'Legal',
    'Letter',
    'Tabloid'
  ]
  
  const marginOptions = [
    'Default',
    'Minimal',
    'None'
  ]
  
  function getMarginValues(marginType: string): { top: number; right: number; bottom: number; left: number } {
    switch (marginType) {
      case 'None':
        return { top: 0, right: 0, bottom: 0, left: 0 }
      case 'Minimal':
        return { top: 0.25, right: 0.25, bottom: 0.25, left: 0.25 }
      default: // Default
        return { top: 0.6, right: 0.6, bottom: 0.6, left: 0.6 }
    }
  }
  
  async function handleExport() {
    const margins = getMarginValues(margin)
    
    // Send raw content to backend - let the backend handle markdown conversion
    const options = {
      fileName: fileName.replace(/\.md$/, ''),
      content: content, // Send raw markdown/content
      includeTitle,
      pageSize: pageSize.toLowerCase(),
      landscape,
      margins,
      scale: downscaleValue / 100
    }
    
    try {
      // Call the IPC handler to export PDF
      if (window.api?.exportToPDF) {
        const result = await window.api.exportToPDF(options)
        if (result.success) {
          dispatch('success', { filePath: result.filePath })
          close()
        } else {
          dispatch('error', { error: result.error })
        }
      } else {
        console.error('Export to PDF API not available')
        dispatch('error', { error: 'PDF export not available' })
      }
    } catch (error) {
      console.error('Failed to export PDF:', error)
      dispatch('error', { error })
    }
  }
  
  // Markdown conversion is now handled in the backend using the 'marked' library
  
  function close() {
    isOpen = false
    dispatch('close')
  }
  
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      close()
    }
  }
  
  onMount(() => {
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  })
</script>

{#if isOpen}
  <div 
    class="pdf-export-overlay" 
    onclick={close}
    onkeydown={(e) => e.key === 'Escape' && close()}
    role="button"
    tabindex="-1"
    aria-label="Close dialog overlay"
  >
    <div 
      class="pdf-export-dialog" 
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.key === 'Escape' && close()}
      role="dialog"
      aria-labelledby="pdf-export-title"
      tabindex="0"
    >
      <div class="dialog-header">
        <h2 id="pdf-export-title">Export to PDF</h2>
        <button class="close-button" onclick={close}>
          <X size={20} />
        </button>
      </div>
      
      <div class="dialog-content">
        <div class="export-info-section">
          <p class="export-info">
            Export <strong>"{fileName || 'Untitled'}"</strong> to PDF with the settings below.
          </p>
        </div>
        
        <div class="setting-group">
          <label class="setting-row">
            <span>Include file name as title</span>
            <input 
              type="checkbox" 
              bind:checked={includeTitle} 
              class="toggle-switch"
            />
          </label>
        </div>
        
        <div class="setting-group">
          <label class="setting-row">
            <span>Page size</span>
            <select bind:value={pageSize} class="setting-select">
              {#each pageSizeOptions as size}
                <option value={size}>{size}</option>
              {/each}
            </select>
          </label>
        </div>
        
        <div class="setting-group">
          <label class="setting-row">
            <span>Landscape</span>
            <input 
              type="checkbox" 
              bind:checked={landscape} 
              class="toggle-switch"
            />
          </label>
        </div>
        
        <div class="setting-group">
          <label class="setting-row">
            <span>Margin</span>
            <select bind:value={margin} class="setting-select">
              {#each marginOptions as marginOption}
                <option value={marginOption}>{marginOption}</option>
              {/each}
            </select>
          </label>
        </div>
        
        <div class="setting-group">
          <div class="setting-row no-border">
            <span>Downscale percent</span>
            <div class="downscale-control">
              <input 
                type="range" 
                bind:value={downscaleValue} 
                min="50" 
                max="100" 
                step="1"
                class="downscale-slider"
                onmouseover={() => showTooltip = true}
                onmouseout={() => {
                  showTooltip = false
                  isHoveringThumb = false
                }}
                onfocus={() => showTooltip = true}
                onblur={() => showTooltip = false}
                onmousemove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const x = e.clientX - rect.left
                  const percent = x / rect.width
                  
                  // Calculate the thumb position
                  const thumbPos = ((downscaleValue - 50) / 50) * rect.width
                  const thumbRadius = 6 // Half of thumb width (12px)
                  
                  // Check if hovering over thumb (current value indicator)
                  isHoveringThumb = Math.abs(x - thumbPos) < thumbRadius
                  
                  // Calculate value at mouse position
                  const valueAtMouse = Math.round(50 + (percent * 50))
                  tooltipValue = isHoveringThumb ? downscaleValue : Math.max(50, Math.min(100, valueAtMouse))
                  
                  tooltipPosition = {
                    x: x,
                    y: -25
                  }
                }}
              />
              {#if showTooltip}
                <div 
                  class="slider-tooltip" 
                  class:thumb-hover={isHoveringThumb}
                  style="left: {tooltipPosition.x}px; top: {tooltipPosition.y}px"
                >
                  {tooltipValue}%
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>
      
      <div class="dialog-footer">
        <button class="button button-primary" onclick={handleExport}>
          Export to PDF
        </button>
        <button class="button button-secondary" onclick={close}>
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .pdf-export-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(2px);
  }
  
  .pdf-export-dialog {
    background: var(--bg-primary);
    border-radius: 10px;
    width: 460px;
    max-width: 90vw;
    border: 1px solid var(--border-primary);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  }
  
  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .dialog-header h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .close-button {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
  }
  
  .close-button:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .dialog-content {
    padding: 0;
  }
  
  .export-info-section {
    padding: 16px 20px;
    border-bottom: none;
  }
  
  .export-info {
    margin: 0;
    color: var(--text-secondary);
    font-size: 13px;
  }
  
  .export-info strong {
    color: var(--text-primary);
    font-weight: 500;
  }
  
  .setting-group {
    margin-bottom: 0;
    padding: 0 20px;
  }
  
  .setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--border-secondary);
    font-size: 13px;
    color: var(--text-primary);
  }
  
  .setting-row.no-border {
    border-bottom: none;
  }
  
  .downscale-control {
    display: flex;
    align-items: center;
    position: relative;
  }
  
  .slider-tooltip {
    position: absolute;
    background: var(--bg-primary);
    color: var(--text-primary);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transform: translateX(-50%);
    z-index: 10;
    transition: all 0.1s ease;
  }
  
  .slider-tooltip.thumb-hover {
    background: var(--accent-primary);
    color: white;
    font-weight: 600;
  }
  
  .downscale-slider {
    width: 80px;
    height: 3px;
    -webkit-appearance: none;
    appearance: none;
    background: #B0B0B0;
    border-radius: 2px;
    outline: none;
    cursor: pointer;
    position: relative;
  }
  
  :global(.dark) .downscale-slider {
    background: #606060;
  }
  
  /* Remove webkit track styles - using base element for track */
  
  .downscale-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    border: 1px solid #B0B0B0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  
  :global(.dark) .downscale-slider::-webkit-slider-thumb {
    background: white;
    border: 1px solid #606060;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  /* Remove moz track styles - using base element for track */
  
  .downscale-slider::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: white;
    border-radius: 50%;
    border: 1px solid #B0B0B0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    cursor: pointer;
  }
  
  :global(.dark) .downscale-slider::-moz-range-thumb {
    background: white;
    border: 1px solid #606060;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  .toggle-switch {
    appearance: none;
    width: 38px;
    height: 20px;
    background: var(--bg-tertiary);
    border-radius: 10px;
    position: relative;
    cursor: pointer;
    border: 1px solid var(--border-primary);
    transition: all 0.2s;
  }
  
  .toggle-switch:checked {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
  }
  
  .toggle-switch::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 1px;
    left: 1px;
    transition: all 0.2s;
  }
  
  .toggle-switch:checked::after {
    left: 20px;
  }
  
  .setting-select {
    appearance: none;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-primary);
    border-radius: 5px;
    padding: 4px 28px 4px 10px;
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 5L6 8L9 5" stroke="%23666" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    background-repeat: no-repeat;
    background-position: right 6px center;
    transition: all 0.2s;
  }
  
  /* Hover effect removed per user request */
  
  .setting-select:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 2px rgba(var(--accent-primary-rgb), 0.2);
  }
  
  /* .number-input styles removed - unused */
  
  .dialog-footer {
    display: flex;
    gap: 10px;
    padding: 14px 20px;
    border-top: none;
    justify-content: flex-end;
  }
  
  .button {
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
  }
  
  .button-primary {
    background: var(--accent-primary, #007AFF);
    color: white;
  }
  
  .button-primary:hover {
    background: var(--accent-primary-hover, #0051D5);
    opacity: 0.95;
  }
  
  :global(.dark) .button-primary {
    background: #0A84FF;
  }
  
  :global(.dark) .button-primary:hover {
    background: #0066CC;
    opacity: 1;
  }
  
  .button-secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
  }
  
  .button-secondary:hover {
    background: var(--bg-secondary);
    border-color: var(--border-tertiary);
  }
</style>